import {
  AppBar,
  Button,
  Checkbox,
  Grid,
  InputAdornment,
  InputBase,
  inputBaseClasses,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { DocumentDownload, Eye, Magnifier } from 'asset';
import {
  DeleteDialog,
  FilterMenu,
  HeadTable,
  Layout,
  PopupCreateOrEditSitter,
  TableCaption,
} from 'components';
import DeleteAllDialog from 'components/common/DeleteAllDialog';
import { ButtonDelete, ButtonEdit } from 'components/common/table';
import PopupWarningApprove from 'components/Popups/Warning/PopupWarningApprove';
import { drawerWidth } from 'config';
import FileSaver from 'file-saver';
import { getSortItem, NotifyService, printPrice } from 'helpers';
import useDebounce from 'hooks/useDebounce';
import {
  DELETE_DIALOG_TYPE,
  FilterValue,
  GET_SITTERS_PARAMS_REQUEST,
  SITTER_ITEM_TYPE,
  SITTER_STATUS,
  TYPE_POPUP,
} from 'models';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  deleteAllSitterApi,
  getSittersExportCSVApi,
  updateManySitterStatusApi,
  updateSitterStatusApi,
} from 'services';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setSitterListACParams } from 'store/reducers/accounts';
import { getSitterListACRequest } from 'store/reducers/accounts/actionTypes';
import { theme } from 'theme';
import { filterOptions, headCells } from './data';

export const SitterListAC = () => {
  const dispatch = useAppDispatch();
  const { sitter_list_ac } = useAppSelector(
    (state) => state.accountReducer.sitters
  );
  const { params } = sitter_list_ac;

  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(false);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [keyword, setKeyword] = useState('');

  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [isOpenCreate, setIsOpenCreate] = useState<boolean>(false);
  const [editItem, setEditItem] = useState<SITTER_ITEM_TYPE>();
  const [deleteItem, setDeleteItem] = useState<SITTER_ITEM_TYPE>();
  const [openDeleteAll, setOpenDeleteAll] = useState<boolean>(false);
  const [openApprove, setOpenApprove] = useState<boolean>(false);
  const [openApproveAll, setOpenApproveAll] = useState<boolean>(false);

  const onSetListParams = (params: GET_SITTERS_PARAMS_REQUEST | null) => {
    dispatch(setSitterListACParams(params));
  };

  useEffect(() => {
    setIsInitialLoad(true);
    setKeyword(params?.keyword ?? '');

    if (!params) {
      onSetListParams({
        sort: 'createdAt-desc',
        page: 1,
        limit: 50,
        is_joined_ac: true,
      });
    }

    if (sitter_list_ac.records.length === 0) {
      dispatch(
        getSitterListACRequest(
          params ?? {
            sort: 'createdAt-desc',
            page: 1,
            limit: 50,
            is_joined_ac: true,
          }
        )
      );
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (params && isInitialLoad) {
      dispatch(getSitterListACRequest(params));
    }
    // eslint-disable-next-line
  }, [params, dispatch]);

  const setOpenEdit = (event: any, item: SITTER_ITEM_TYPE) => {
    event.stopPropagation();
    setEditItem(item);
  };

  const setOpenDelete = (event: any, item: SITTER_ITEM_TYPE) => {
    event.stopPropagation();
    setDeleteItem(item);
    setOpenDeletePopup(true);
  };

  const onClosePopup = (type: TYPE_POPUP) => {
    switch (type) {
      case TYPE_POPUP.DELETE:
        setOpenDeletePopup(false);
        setDeleteItem(undefined);
        break;
      case TYPE_POPUP.CREATE_OR_EDIT:
        setIsOpenCreate(false);
        setEditItem(undefined);
        break;
      case TYPE_POPUP.DELETE_ALL:
        setOpenDeleteAll(false);
        break;
      case TYPE_POPUP.APPROVE_ALL:
        setOpenApproveAll(false);
        break;
      case TYPE_POPUP.APPROVE:
        setOpenApprove(false);
        break;
    }
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetListParams({ ...params, page: 1, limit });
  };

  const { items } = usePagination({
    count: sitter_list_ac.metadata.total_pages,
    page: sitter_list_ac.metadata.page,
    onChange(_event, page) {
      onSetListParams({ ...params, page: page });
    },
  });

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const onSetKeywordParams = useDebounce((keyword: string) => {
    const _params = { ...params, page: 1 };
    if (keyword) _params.keyword = keyword.trim();
    else delete _params.keyword;
    onSetListParams(_params);
  }, 250);

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
    onSetKeywordParams(event.target.value);
  };

  const handleClick = (_: any, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: readonly string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      console.log(event.target.checked);
      const newSelected = sitter_list_ac.records.map((n) => n.sitter_id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const filterValue: FilterValue = useMemo(() => {
    return {
      first_name: sitter_list_ac.params?.first_name,
      last_name: sitter_list_ac.params?.last_name,
      email: sitter_list_ac.params?.email,
      pending_payout: sitter_list_ac.params?.pending_payout,
    };
  }, [sitter_list_ac.params]);

  const onChangeFilter = (value: FilterValue) => {
    setSelected([]);
    const _params = { ...params };
    delete _params.first_name;
    delete _params.last_name;
    delete _params.email;
    delete _params.pending_payout;
    onSetListParams({
      ..._params,
      ...value,
      page: 1,
    });
  };

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: keyof SITTER_ITEM_TYPE
  ) => {
    setSelected([]);
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetListParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  const onExportCSV = async () => {
    if (!params) return;

    await getSittersExportCSVApi({
      sort: params.sort,
      ...params,
    })
      .then((res) => {
        FileSaver.saveAs(
          res.data,
          `${moment().format('YYYY-MM-DD')} Sitters Joined Aunty Concierge.csv`
        );
      })
      .catch((e) => NotifyService.error(e));
  };

  const approveSitter = async () => {
    if (!deleteItem?.sitter_id) return;
    await updateSitterStatusApi(deleteItem?.sitter_id, SITTER_STATUS.APPROVED)
      .then((r) => {
        onReloadList();
        onClosePopup(TYPE_POPUP.APPROVE);
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {});
  };

  const approveManySitter = async () => {
    await updateManySitterStatusApi({
      sitterIds: [...selected],
      status: SITTER_STATUS.APPROVED,
    })
      .then((r) => {
        onReloadList();
        onClosePopup(TYPE_POPUP.APPROVE_ALL);
        setSelected([]);
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {});
  };

  const deleteManySitter = async () => {
    const sitterIds: any = selected;
    await deleteAllSitterApi(sitterIds)
      .then((r) => {
        onReloadList();
        onClosePopup(TYPE_POPUP.DELETE_ALL);
        setSelected([]);
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {});
  };

  const onReloadList = () => {
    if (params) dispatch(getSitterListACRequest(params));
  };

  const sortItem = useMemo(() => {
    return getSortItem(sitter_list_ac.params?.sort);
  }, [sitter_list_ac.params]);

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '10px',
            overflow: 'auto',
            paddingY: 1,
          }}
        >
          <Grid mr={2}>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
                whiteSpace: 'nowrap',
              }}
            >
              Concierge / Sitters
            </Typography>
          </Grid>
          <Grid display="flex" gap="8px" flexGrow={1} justifyContent="center">
            <InputBase
              placeholder="Search"
              sx={{
                maxWidth: 370,
                minWidth: 200,
                flexGrow: 1,
                background: '#F2F4F6',
                border: '1px solid #D9D9D9',
                p: '0px 7px 0px 11px',
                borderRadius: '5px',
                [`& .${inputBaseClasses.input}`]: {
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '100%',
                  p: 0,
                  height: 'fit-content',
                },
              }}
              onChange={onChangeSearch}
              value={keyword}
              endAdornment={
                <InputAdornment position="end">
                  <Magnifier />
                </InputAdornment>
              }
            />
            <FilterMenu
              filterOptions={filterOptions}
              filterValue={filterValue}
              onChange={onChangeFilter}
            />
          </Grid>
          <Grid display="flex" gap="8px">
            <Button
              sx={{
                width: '131px',
                height: 30,
                fontWeight: 600,
                fontSize: '13px',
                border: `1px solid ${theme.palette.secondary.main}`,
                p: '0px',
              }}
              variant="outlined"
              color="secondary"
              endIcon={<DocumentDownload />}
              onClick={onExportCSV}
            >
              Export to CSV
            </Button>
          </Grid>
        </Toolbar>
      </AppBar>
      <Stack spacing="8px">
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 150px)',
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
          >
            <HeadTable<SITTER_ITEM_TYPE>
              numSelected={selected.length}
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={sitter_list_ac.records.length}
              headCells={headCells}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {sitter_list_ac.records.map((row, index) => {
                const isItemSelected = isSelected(row.sitter_id);
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.sitter_id ?? index}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                      }}
                    >
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onChange={(e) => handleClick(e, row.sitter_id)}
                        inputProps={{
                          'aria-labelledby': labelId,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {`${row.first_name} ${row.last_name} (${row.email})`}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.phone_number}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                        color: row.rating < 5 ? 'red.light' : 'black.light',
                      }}
                    >
                      {row.rating}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.payout_pending ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {printPrice(row.ac_balance)}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.payoutMethod?.pay_now_name}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.payoutMethod?.pay_now_number}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        textTransform="capitalize"
                      >
                        <Grid
                          sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            marginRight: '5px',
                            backgroundColor:
                              row.status === 'active'
                                ? theme.palette.green.light
                                : row.status === 'pending'
                                ? 'yellow.light'
                                : '#B6B6B6',
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 400,
                          }}
                        >
                          {row.status}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      <Stack direction="row" spacing="10px">
                        <Link to={`/concierge/sitters-ac/${row.sitter_id}`}>
                          <Button
                            sx={{
                              minWidth: '38px !important',
                              height: '32px',
                              '&.MuiButton-root': {
                                p: '0px !important',
                              },
                            }}
                            variant="contained"
                            color="secondary"
                          >
                            <Eye />
                          </Button>
                        </Link>
                        <ButtonEdit onClick={(e) => setOpenEdit(e, row)} />
                        <ButtonDelete onClick={(e) => setOpenDelete(e, row)} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          rowsPerPage={params?.limit ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={sitter_list_ac.metadata}
          items={items}
        />
      </Stack>
      <PopupCreateOrEditSitter
        cleanDetail={true}
        open={isOpenCreate || !!editItem}
        subject={editItem?.sitter_id}
        onSuccess={onReloadList}
        handleClosePopup={() => onClosePopup(TYPE_POPUP.CREATE_OR_EDIT)}
        isSitterAC
      />
      {openDeletePopup && (
        <DeleteDialog
          open={openDeletePopup}
          onClose={() => onClosePopup(TYPE_POPUP.DELETE)}
          subject={deleteItem?.sitter_id}
          onSuccess={onReloadList}
          type={DELETE_DIALOG_TYPE.SITTER}
          name={deleteItem?.first_name}
        />
      )}
      {openDeleteAll && (
        <DeleteAllDialog
          open={openDeleteAll}
          onClose={() => setOpenDeleteAll(false)}
          onClickDelete={deleteManySitter}
        />
      )}
      {openApprove && (
        <PopupWarningApprove
          open={openApprove}
          onClose={() => setOpenApprove(false)}
          onClickApprove={approveSitter}
        />
      )}
      {openApproveAll && (
        <PopupWarningApprove
          open={openApproveAll}
          onClose={() => setOpenApproveAll(false)}
          onClickApprove={approveManySitter}
        />
      )}
    </Layout>
  );
};

export default SitterListAC;
